import React, { useState } from "react";
import { TooltipsIcon } from "../../assets/icons";
import axios from "axios";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { reqToUpdateSettings } from "../../redux/slices.js/settings/settingsSlice";

const QuotePolicies = ({ settings = [] }) => {
  const dispatch = useDispatch();

  // Convert API array → object { name: value }
  const initialState = settings.reduce((acc, s) => {
    acc[s.name] = s.value;
    return acc;
  }, {});

  const [formValues, setFormValues] = useState({
    quote_validity_days: "",
    minimum_quote_values: "",
    enable_quote_system: false,
    require_digital_signature: false,
    auto_approve_std_quote: false,
    // approval workflow will now be stored as a single string
    approveal_workflow: "automatic_approval",
    ...initialState,
  });

  const quoteToggles = [
    { id: "enable_quote_system", label: "Enable quote system" },
  ];

  const quoteSettings = [
    { id: "require_digital_signature", label: "Require digital signature" },
    { id: "auto_approve_std_quote", label: "Auto-approve standard quotes" },
  ];

  const approvalWorkflowOptions = [
    { label: "Automatic Approval", value: "automatic_approval" },
    { label: "Manual Review Required", value: "manual_review_required" },
    { label: "Threshold-based", value: "threshold_based" },
  ];

  const handleToggle = (id) => {
    setFormValues((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleWorkflowChange = (value) => {
    setFormValues((prev) => ({
      ...prev,
      approveal_workflow: value, // store only one selected value
    }));
  };

  const handleSaveChanges = () => {
    const payload = Object.entries(formValues).map(([name, value]) => ({
      name,
      value: value instanceof Date ? value.toISOString() : value,
    }));

    dispatch(
      reqToUpdateSettings({
        data: payload,
        onSuccess: (response) => {
          toast.success("Quote policies updated!");
        },
        onFailure: (error) => {
          toast.error("Failed to update settings");
        },
      })
    );
  };

  return (
    <>
      <div className="space-y-6">
        {/* Quote validity & min value */}
        <div className="grid gap-4 md:gap-6 md:grid-cols-2">
          <div className="form-group">
            <label>
              Quote Validity (days) <span>*</span>
            </label>
            <div className="relative">
              <input
                type="number"
                placeholder="3 attempts recommended"
                className="pe-5"
                value={formValues.quote_validity_days}
                onChange={(e) =>
                  handleChange("quote_validity_days", e.target.value)
                }
              />
              <span className="absolute right-3 top-3 h-5 w-5 text-gray-500 pointer-events-none">
                {TooltipsIcon}
              </span>
            </div>
          </div>

          <div className="form-group">
            <label>
              Minimum Quote Value <span>*</span>
            </label>
            <input
              type="number"
              placeholder="7 days recommended"
              value={formValues.minimum_quote_values}
              onChange={(e) =>
                handleChange("minimum_quote_values", e.target.value)
              }
            />
          </div>
        </div>

        {/* Quote System Toggle */}
        <div className="grid gap-4">
          {quoteToggles.map((toggle) => (
            <div
              key={toggle.id}
              className="flex items-center justify-start gap-4"
            >
              <button
                type="button"
                role="switch"
                aria-checked={formValues[toggle.id]}
                onClick={() => handleToggle(toggle.id)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                  formValues[toggle.id] ? "bg-[#4285F4]" : "bg-[#E5E7EB]"
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                    formValues[toggle.id]
                      ? "translate-x-[10px]"
                      : "translate-x-0"
                  }`}
                />
              </button>
              <span className="text-black text-span">{toggle.label}</span>
            </div>
          ))}
        </div>

        {/* Approval Workflow */}
        <div className="form-group">
          <label>
            Approval Workflow <span>*</span>
          </label>
          <div className="flex space-x-4 items-center mt-4">
            {approvalWorkflowOptions.map((option) => (
              <label
                key={option.value}
                className="flex items-center cursor-pointer"
              >
                <input
                  type="radio"
                  name="approval-workflow"
                  value={option.value}
                  checked={formValues.approveal_workflow === option.value}
                  onChange={() => handleWorkflowChange(option.value)}
                  className="hidden"
                />
                <span
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    formValues.approveal_workflow === option.value
                      ? "bg-blue-500 border-blue-500"
                      : "bg-white border-gray-300"
                  }`}
                >
                  {formValues.approveal_workflow === option.value && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-white"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 6.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </span>
                <div className="ml-2 text-black text-span">{option.label}</div>
              </label>
            ))}
          </div>
        </div>

        {/* Quote Settings */}
        <div className="form-group">
          <label className="!text-black pb-2">Settings</label>
          <div className="grid gap-4">
            {quoteSettings.map((toggle) => (
              <div
                key={toggle.id}
                className="flex items-center justify-start gap-4"
              >
                <button
                  type="button"
                  role="switch"
                  aria-checked={formValues[toggle.id]}
                  onClick={() => handleToggle(toggle.id)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 transition ${
                    formValues[toggle.id] ? "bg-[#4285F4]" : "bg-[#E5E7EB]"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                      formValues[toggle.id]
                        ? "translate-x-[10px]"
                        : "translate-x-0"
                    }`}
                  />
                </button>
                <span className="text-black text-span">{toggle.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-auto flex gap-3 pt-6 justify-end flex-wrap">
        <button type="button" className="next-btn" onClick={handleSaveChanges}>
          Save quote policies
        </button>
      </div>
    </>
  );
};

export default QuotePolicies;
